import { useState, useEffect, useRef } from "react";
import { TrainingData, RaceTarget, PlannedWeek, PlannedSession, SportType, PlannerProfile, RaceDistance, SPORTS } from "../types/training";
import { CalendarEvent } from "../types/training";
import { RacePlan } from "../PRESENTER/trainingPlanPage";
import { SPORT_LABELS, SPORT_ABBREV, isEnduranceSport } from "../COMPONENTS/plannerProfile";
import { nextSnowSeason } from "../COMPONENTS/capacityPlan";
import { PlanAction, PlanActionOverrides, overrideKey, loadPlanActionOverrides, setPlanActionOverride } from "../COMPONENTS/planActionOverrides";
import { flushCloudSync } from "../COMPONENTS/cloudSync";
import { calcIntensityRatio, parseStructuredWorkout, inferRaceDistance } from "../COMPONENTS/planGenerator";
import { parseGoalHours } from "../COMPONENTS/raceTime";
import { diffPlanVsCalendar } from "../COMPONENTS/planCalendarDiff";
import { WorkoutChart as SharedWorkoutChart } from "../COMPONENTS/WorkoutChart";
import { PlannerFlowChart } from "../COMPONENTS/PlannerFlowChart";
import { IntervalBankPanel } from "../COMPONENTS/IntervalBankPanel";
import { IronManPanel } from "../COMPONENTS/IronManPanel";
import "../CSS/trainingPlan.scss";
import { usePageBackground } from "../COMPONENTS/usePageBackground";

// ── Race detection (mirrors TrainingPageView) ────────────────────────────────

function isRaceEvent(e: CalendarEvent): boolean {
  if (!e.category) return false;
  const cat = e.category.toUpperCase();
  return cat === "RACE" || /^RACE_[A-D]$/.test(cat);
}

// intervals.icu encodes race priority in the category (RACE_A/B/C). Default to
// A (season target) when the category is a bare "RACE".
function racePriority(e: CalendarEvent): "A" | "B" | "C" {
  const m = (e.category ?? "").toUpperCase().match(/^RACE_([ABC])$/);
  return (m?.[1] as "A" | "B" | "C") ?? "A";
}

// ── Polarization helpers ──────────────────────────────────────────────────────

const HARD_KEYWORDS = /interval|intervall|threshold|tröskel|z4|z5|sprint|fartlek|backe|hill|kvalitet/i;
const EASY_OVERRIDE = /pendling|commute|lätt|easy|lugn|vila|recovery/i;

function parseDurationHours(text: string): number {
  const t = text.toLowerCase();
  // Handles the compact "1h30m" form; excludes a preceding digit so the "0m"
  // inside "30m" isn't read as 0 minutes (see chartMetrics.parseDurationHours).
  const h = t.match(/(?<![a-z\d])(\d+(?:[.,]\d+)?)\s*h(?:ours?)?/);
  if (h) {
    const rest = t.slice(t.indexOf(h[0]) + h[0].length);
    const m2 = rest.match(/^\s*(\d+)\s*m(?:in)?/);
    return parseFloat(h[1].replace(",", ".")) + (m2 ? parseInt(m2[1]) / 60 : 0);
  }
  const m = t.match(/(?<![a-z\d])(\d+)\s*m(?:in)?\b/);
  if (m) return parseInt(m[1]) / 60;
  return 0;
}

function eventDurationHours(e: CalendarEvent): number {
  const raw = e as any;
  if (raw.duration && raw.duration > 0) return raw.duration / 3600;
  const desc = (e.description ?? "") as string;
  if (desc.includes("\n")) {
    const parsed = parseStructuredWorkout(desc);
    if (parsed) return parsed.durationHours;
  }
  return parseDurationHours(`${e.name ?? ""} ${desc}`);
}

function isoWeekKeyOf(dateStr: string): string {
  const d = new Date(dateStr.slice(0, 10) + "T12:00:00");
  const utc = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((utc.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${utc.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

function buildCombinedHardPct(
  weeks: PlannedWeek[],
  sessions: PlannedSession[],
): { hardPctByWeek: Record<string, number>; totalHardH: number; totalH: number } {
  // The plan is the complete schedule; existing non-race events get wiped on
  // publish, so only the plan's own sessions count toward weekly hours / hard%.
  const data: Record<string, { hard: number; total: number }> = {};

  for (const s of sessions) {
    if (!data[s.weekKey]) data[s.weekKey] = { hard: 0, total: 0 };
    data[s.weekKey].total += s.durationHours;
    if (s.targetIF >= 0.87) data[s.weekKey].hard += s.durationHours;
  }

  const hardPctByWeek: Record<string, number> = {};
  let totalHardH = 0, totalH = 0;
  for (const w of weeks) {
    const d = data[w.weekKey];
    hardPctByWeek[w.weekKey] = d && d.total > 0 ? Math.round((d.hard / d.total) * 100) : 0;
    totalHardH += d?.hard ?? 0;
    totalH     += d?.total ?? 0;
  }
  return { hardPctByWeek, totalHardH, totalH };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// Goal-form sport options: the full registry (label-sorted).
const SPORT_OPTIONS: { value: SportType; label: string }[] = SPORTS
  .map(s => ({ value: s.value as SportType, label: s.label }))
  .sort((a, b) => a.label.localeCompare(b.label, "sv"));

// Non-endurance sports use the capacity engine and support a competition PERIOD
// (an end date) rather than a single race day.
const NON_ENDURANCE = new Set<SportType>(SPORTS.filter(s => s.category === "explosive").map(s => s.value));

const PHASE_LABELS: Record<string, string> = {
  base: "Base", build: "Build", cutback: "Cutback", peak: "Peak", taper: "Taper", race: "Race",
};

// Distance is picked in km (10K chunks). km + expected time are passed straight
// to the planner, which scales the long run continuously (no road buckets).
const DISTANCE_KM_OPTIONS = [5, 10, 20, 30, 40, 50, 60, 70, 80, 90];

// Representative km for an inferred band, shown in the "Auto" option label.
const BAND_KM: Record<RaceDistance, string> = {
  "5k": "5 km", "10k": "10 km", half: "~21 km", marathon: "~42 km", ultra: "70+ km", unknown: "?",
};

function fmtDuration(hours: number): string {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

function fmtDate(dateStr: string): string {
  const d = new Date(dateStr + "T12:00:00");
  const days = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  return `${days[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function WeeksTable({ weeks, hardPctByWeek }: { weeks: PlannedWeek[]; hardPctByWeek: Record<string, number> }) {
  const durationMode = weeks.some(w => w.targetHours != null);

  return (
    <div className="tp-weeks">
      <table className="tp-weeks__table">
        <thead>
          <tr>
            <th>Week</th>
            <th>Phase</th>
            <th className="tp-weeks__tss">{durationMode ? "Tid" : "TSS"}</th>
            <th className="tp-weeks__tss">Z4+%</th>
          </tr>
        </thead>
        <tbody>
          {weeks.map(w => {
            const hardPct = hardPctByWeek[w.weekKey] ?? 0;
            const load = durationMode
              ? (w.targetHours != null ? fmtDuration(w.targetHours) : "—")
              : String(w.targetTSS);
            return (
              <tr key={w.weekKey} className={`tp-weeks__row tp-weeks__row--${w.phase}`}>
                <td>{w.label}</td>
                <td><span className={`tp-weeks__badge tp-weeks__badge--${w.phase}`}>{PHASE_LABELS[w.phase]}</span></td>
                <td className="tp-weeks__tss">{load}</td>
                <td className="tp-weeks__tss">{hardPct > 0 ? `${hardPct}%` : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

type SessionStatus = "new" | "kept" | "removed" | "race" | "replaced";

interface DisplayRow {
  date: string;
  name: string;
  sport: string;
  durationHours: number;
  tss: number | null;
  status: SessionStatus;
  description?: string;
  flagReason?: string;
  pinned?: PlanAction;   // user override on the planned action (protect / remove)
}

function estimateCalTSS(e: CalendarEvent): number {
  const desc = (e.description ?? "") as string;
  if (desc.includes("\n")) {
    const parsed = parseStructuredWorkout(desc);
    if (parsed) return parsed.tss;
  }
  const hours = eventDurationHours(e);
  if (hours <= 0) return 0;
  const text = `${e.name ?? ""} ${desc}`;
  const isHard = HARD_KEYWORDS.test(text) && !EASY_OVERRIDE.test(text);
  const if_ = isHard ? 0.88 : 0.70;
  return Math.round(if_ * if_ * hours * 100);
}

function buildDisplayRows(
  planSessions: PlannedSession[],
  calendarEvents: CalendarEvent[],
  flaggedDates: Map<string, string>,
  rangeStart: string,
  rangeEnd: string,
  blockedDates: Set<string>,
  gymDays: Set<number>,
  overrides: PlanActionOverrides = {},
): DisplayRow[] {
  // Colour every chip by the actual publish diff (diffPlanVsCalendar, the same
  // signature reconcile uses): a plan session already on intervals.icu unchanged
  // is KEPT (grey); a session not yet there is NEW (green); an in-window non-race
  // event the plan no longer wants is REMOVED (red); races are never touched.
  const rows: DisplayRow[] = [];
  const raceDates = new Set<string>();

  for (const e of calendarEvents) {
    const date = e.start_date_local.slice(0, 10);
    if (date < rangeStart || date > rangeEnd || !isRaceEvent(e)) continue;
    raceDates.add(date);
    rows.push({
      date, name: e.name ?? "Lopp",
      sport: (e as any).type ?? "",
      durationHours: eventDurationHours(e),
      tss: null,
      status: "race",
      description: (e as any).description ?? undefined,
    });
  }

  const windowEvents = calendarEvents.filter(e => {
    const date = e.start_date_local.slice(0, 10);
    return date >= rangeStart && date <= rangeEnd && !isRaceEvent(e);
  });
  const sessionsForDiff = planSessions.filter(s => !raceDates.has(s.date)); // race day — leave the race

  const { sessionKept, removedEventIds } = diffPlanVsCalendar(
    sessionsForDiff.map(s => ({ date: s.date, name: s.name, sport: s.sport, description: s.description })),
    windowEvents.map(e => ({
      id: e.id, date: e.start_date_local.slice(0, 10),
      name: e.name ?? "", type: (e as any).type ?? "", description: (e as any).description ?? "",
    })),
  );

  sessionsForDiff.forEach((s, i) => {
    rows.push({
      date: s.date, name: s.name,
      sport: SPORT_ABBREV[s.sport] ?? s.sport,
      durationHours: s.durationHours,
      tss: s.estimatedTSS,
      status: sessionKept[i] ? "kept" : "new",
      description: s.description,
      flagReason: flaggedDates.get(s.date),
    });
  });

  for (const e of windowEvents) {
    if (!removedEventIds.has(e.id)) continue;
    rows.push({
      date: e.start_date_local.slice(0, 10), name: e.name ?? "Pass",
      sport: (e as any).type ?? "",
      durationHours: eventDurationHours(e),
      tss: estimateCalTSS(e),
      status: "removed",
      description: (e as any).description ?? undefined,
    });
  }

  // Reflect user overrides: a protected removal shows as kept; a session pinned
  // for removal shows as removed. Publish (reconcile) applies the same overrides.
  for (const row of rows) {
    if (row.status === "race") continue;
    const o = overrides[overrideKey(row.date, row.name)];
    if (!o) continue;
    row.pinned = o;
    if (o === "protect" && row.status === "removed") row.status = "kept";
    else if (o === "remove" && (row.status === "new" || row.status === "kept")) row.status = "removed";
  }

  return rows.sort((a, b) => a.date.localeCompare(b.date));
}

// Workout profile chart — shared component (keeps the pc__wchart styling here).
function WorkoutChart(props: { description: string; durationHours: number; name?: string }) {
  return <SharedWorkoutChart {...props} className="pc__wchart" />;
}

const PC_DAY_LABELS = ["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"];

function PlanCalendar({ sessions, planWeeks, calendarEvents, flaggedDates, rangeStart, rangeEnd, blockedDates, gymDays }: {
  sessions: PlannedSession[];
  planWeeks: PlannedWeek[];
  calendarEvents: CalendarEvent[];
  flaggedDates: Map<string, string>;
  rangeStart: string;
  rangeEnd: string;
  blockedDates: Set<string>;
  gymDays: Set<number>;
}) {
  const [selected, setSelected] = useState<DisplayRow | null>(null);
  const [overrides, setOverrides] = useState<PlanActionOverrides>(() => loadPlanActionOverrides());
  const rows = buildDisplayRows(sessions, calendarEvents, flaggedDates, rangeStart, rangeEnd, blockedDates, gymDays, overrides);

  function pin(row: DisplayRow, value: PlanAction | null) {
    setOverrides(setPlanActionOverride(row.date, row.name, value));
    setSelected(null);
  }
  const { hardPctByWeek } = buildCombinedHardPct(planWeeks, sessions);
  const weekMeta: Record<string, PlannedWeek> = {};
  for (const w of planWeeks) weekMeta[w.weekKey] = w;
  const calDurationMode = planWeeks.some(w => w.targetHours != null);
  const byDate: Record<string, DisplayRow[]> = {};
  for (const r of rows) {
    if (!byDate[r.date]) byDate[r.date] = [];
    byDate[r.date].push(r);
  }

  // Build monday-anchored weeks covering rangeStart → rangeEnd
  const startD = new Date(rangeStart + "T12:00:00");
  const endD   = new Date(rangeEnd   + "T12:00:00");
  const dow0   = (startD.getDay() + 6) % 7;
  let cursor   = new Date(startD.getTime() - dow0 * 86400000);
  const weeks: string[][] = [];
  while (cursor <= endD) {
    weeks.push(Array.from({ length: 7 }, (_, i) =>
      new Date(cursor.getTime() + i * 86400000).toISOString().slice(0, 10)
    ));
    cursor = new Date(cursor.getTime() + 7 * 86400000);
  }

  const todayStr = new Date().toISOString().slice(0, 10);
  let lastMonth = -1;

  return (
    <div className="pc">
      <div className="pc__legend">
        <span className="pc__legend-item pc__legend-item--new">Nytt förslag</span>
        <span className="pc__legend-item pc__legend-item--kept">Behålls</span>
        <span className="pc__legend-item pc__legend-item--removed">Tas bort</span>
        <span className="pc__legend-item pc__legend-item--race">Lopp</span>
      </div>
      <div className="pc__col-headers">
        <div className="pc__week-num-label" />
        {PC_DAY_LABELS.map(l => <div key={l} className="pc__col-label">{l}</div>)}
      </div>
      {weeks.map((days, wi) => {
        const firstInRange = days.find(d => d >= rangeStart) ?? days[0];
        const month = new Date(firstInRange + "T12:00:00").getMonth();
        const showMonth = month !== lastMonth;
        if (showMonth) lastMonth = month;
        const monthLabel = new Date(firstInRange + "T12:00:00")
          .toLocaleDateString("sv-SE", { month: "long", year: "numeric" });
        const wk = isoWeekKeyOf(days[0]);
        const weekNum = wk.replace(/.*-W0?/, "V");
        const meta = weekMeta[wk];
        const hardPct = hardPctByWeek[wk] ?? 0;
        return (
          <div key={wi}>
            {showMonth && <div className="pc__month-label">{monthLabel}</div>}
            <div className="pc__week">
              <div className="pc__week-num">
                <span className="pc__wn-num">{weekNum}</span>
                {meta && <>
                  <span className={`pc__wn-phase pc__wn-phase--${meta.phase}`}>{meta.phase}</span>
                  <span className="pc__wn-tss">{calDurationMode && meta.targetHours != null ? fmtDuration(meta.targetHours) : meta.targetTSS}</span>
                  <span className="pc__wn-z4">{hardPct > 0 ? `${hardPct}%` : "—"}</span>
                </>}
              </div>
              {days.map(dateStr => {
                const inRange = dateStr >= rangeStart && dateStr <= rangeEnd;
                const isToday = dateStr === todayStr;
                const daySessions = byDate[dateStr] ?? [];
                return (
                  <div key={dateStr} className={`pc__day${isToday ? " pc__day--today" : ""}${!inRange ? " pc__day--out" : ""}`}>
                    <span className="pc__day-num">{parseInt(dateStr.slice(8, 10))}</span>
                    {daySessions.map((r, i) => (
                      <div key={i} className={`pc__chip pc__chip--${r.status}`} onClick={() => setSelected(r)}>
                        {r.name}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {selected && (
        <div className="pc__modal-backdrop" onClick={() => setSelected(null)}>
          <div className="pc__modal" onClick={e => e.stopPropagation()}>
            <div className={`pc__modal-status pc__modal-status--${selected.status}`}>
              {selected.status === "new" ? "Nytt förslag" : selected.status === "kept" ? "Behålls — redan på intervals.icu" : selected.status === "race" ? "Lopp" : selected.status === "replaced" ? "Ersätts vid publicering" : "Tas bort vid publicering"}
            </div>
            <div className="pc__modal-name">{selected.name}</div>
            <div className="pc__modal-date">{fmtDate(selected.date)}</div>
            {selected.flagReason && (
              <div className="pc__modal-flag">{selected.flagReason}</div>
            )}
            <div className="pc__modal-row">
              <span>{selected.sport}</span>
              <span>{fmtDuration(selected.durationHours)}</span>
              {selected.tss != null && <span>{selected.tss} TSS</span>}
            </div>
            <WorkoutChart description={selected.description ?? ""} durationHours={selected.durationHours} name={selected.name} />
            {selected.description && (
              <div className="pc__modal-desc">{selected.description}</div>
            )}
            {selected.status !== "race" && (
              <div className="pc__modal-actions">
                <div className="pc__modal-actions-label">
                  Vid publicering{selected.pinned === "protect" ? " — låst: behåll" : selected.pinned === "remove" ? " — låst: ta bort" : ""}:
                </div>
                <div className="pc__modal-actions-row">
                  <button
                    className={`pc__action-btn${selected.pinned === "protect" ? " pc__action-btn--active" : ""}`}
                    onClick={() => pin(selected, selected.pinned === "protect" ? null : "protect")}
                  >Behåll (ta inte bort)</button>
                  <button
                    className={`pc__action-btn${selected.pinned === "remove" ? " pc__action-btn--active" : ""}`}
                    onClick={() => pin(selected, selected.pinned === "remove" ? null : "remove")}
                  >Ta bort</button>
                  {selected.pinned && (
                    <button className="pc__action-btn" onClick={() => pin(selected, null)}>Auto</button>
                  )}
                </div>
              </div>
            )}
            <button className="pc__modal-close" onClick={() => setSelected(null)}>Stäng</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main view ─────────────────────────────────────────────────────────────────

// ── Known races for autocomplete ──────────────────────────────────────────────
// Approximate annual dates (athlete can adjust). month is 1-12.
interface KnownRace { name: string; sport: SportType; month: number; day: number; }
const KNOWN_RACES: KnownRace[] = [
  { name: "Lidingöloppet 30 km",        sport: "Run",        month: 9,  day: 26 },
  { name: "Lidingöloppet 15 km",        sport: "Run",        month: 9,  day: 27 },
  { name: "Stockholm Marathon",         sport: "Run",        month: 5,  day: 30 },
  { name: "Göteborgsvarvet Halvmarathon", sport: "Run",      month: 5,  day: 16 },
  { name: "Stockholm Halvmarathon",     sport: "Run",        month: 9,  day: 13 },
  { name: "Premiärmilen 10 km",         sport: "Run",        month: 4,  day: 25 },
  { name: "Broloppet",                  sport: "Run",        month: 6,  day: 14 },
  { name: "Vasaloppet 90 km",           sport: "NordicSki",  month: 3,  day: 1  },
  { name: "Halvvasan 45 km",            sport: "NordicSki",  month: 2,  day: 27 },
  { name: "Öppet Spår",                 sport: "NordicSki",  month: 2,  day: 22 },
  { name: "Tjejvasan 30 km",            sport: "NordicSki",  month: 2,  day: 21 },
  { name: "Marcialonga 70 km",          sport: "NordicSki",  month: 1,  day: 25 },
  { name: "Engadin Skimarathon",        sport: "NordicSki",  month: 3,  day: 8  },
  { name: "Birkebeinerrennet 54 km",    sport: "NordicSki",  month: 3,  day: 21 },
  { name: "Vätternrundan 315 km",       sport: "Ride",       month: 6,  day: 13 },
  { name: "Vansbrosimningen",           sport: "Run",        month: 7,  day: 4  },
  { name: "Berlin Marathon",            sport: "Run",        month: 9,  day: 27 },
];
// Next upcoming occurrence of an annual race (this year, or next if already past).
function nextOccurrence(month: number, day: number): string {
  const now = new Date();
  let year = now.getFullYear();
  if (new Date(year, month - 1, day) < now) year += 1;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${year}-${p(month)}-${p(day)}`;
}

interface Props {
  data: TrainingData | null;
  plans: RacePlan[];
  profile: PlannerProfile | null;
  onGenerate: (races: RaceTarget[]) => void;
  onCreateRace: (name: string, date: string, sport: SportType) => Promise<void>;
  onPublish: (index: number) => void;
  onSetRacePriority: (eventId: number, priority: "A" | "B" | "C") => void;
  onEditProfile: () => void;
  onBack: () => void;
}

export function TrainingPlanView({ data, plans, profile, onGenerate, onCreateRace, onPublish, onSetRacePriority, onEditProfile, onBack }: Props) {
  usePageBackground();
  const [manualName, setManualName] = useState("");
  const [manualDate, setManualDate] = useState("");
  const [manualEndDate, setManualEndDate] = useState("");
  const [manualSport, setManualSport] = useState<SportType>("Run");
  const [manualIronman, setManualIronman] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualRaces, setManualRaces] = useState<RaceTarget[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [priorityOverrides, setPriorityOverrides] = useState<Record<string, "A" | "B" | "C">>(
    () => { try { return JSON.parse(localStorage.getItem("race-priority-overrides") || "{}"); } catch { return {}; } }
  );
  useEffect(() => { localStorage.setItem("race-priority-overrides", JSON.stringify(priorityOverrides)); }, [priorityOverrides]);
  // Per-race overrides for when the planner can't confidently infer the distance,
  // plus a free-text goal time. Persisted so they survive reloads / regenerations.
  const [distanceKmOverrides, setDistanceKmOverrides] = useState<Record<string, number>>(
    () => { try { return JSON.parse(localStorage.getItem("race-distance-km") || "{}"); } catch { return {}; } }
  );
  const [goalTimeOverrides, setGoalTimeOverrides] = useState<Record<string, string>>(
    () => { try { return JSON.parse(localStorage.getItem("race-goaltime-overrides") || "{}"); } catch { return {}; } }
  );
  const [trailOverrides, setTrailOverrides] = useState<Record<string, boolean>>(
    () => { try { return JSON.parse(localStorage.getItem("race-trail-overrides") || "{}"); } catch { return {}; } }
  );
  // Chosen peak window WITHIN the winter season for the main-sport alpine goal
  // (defaults to the full snow season). { start, end } as "YYYY-MM-DD".
  const [alpinePeak, setAlpinePeak] = useState<{ start?: string; end?: string }>(
    () => { try { return JSON.parse(localStorage.getItem("alpine-peak-window") || "{}"); } catch { return {}; } }
  );
  useEffect(() => { localStorage.setItem("alpine-peak-window", JSON.stringify(alpinePeak)); }, [alpinePeak]);
  useEffect(() => { localStorage.setItem("race-distance-km", JSON.stringify(distanceKmOverrides)); }, [distanceKmOverrides]);
  useEffect(() => { localStorage.setItem("race-goaltime-overrides", JSON.stringify(goalTimeOverrides)); }, [goalTimeOverrides]);
  useEffect(() => { localStorage.setItem("race-trail-overrides", JSON.stringify(trailOverrides)); }, [trailOverrides]);

  const raceEvents = data?.events.filter(isRaceEvent) ?? [];
  const raceKey = (r: RaceTarget) => r.eventId != null ? `e${r.eventId}` : `${r.name}|${r.date}`;

  // Blocked (vacation) dates from the current profile — used to mark existing
  // sessions in those ranges as removable, independent of plan regeneration.
  const blockedDates = new Set<string>();
  for (const r of profile?.blockedRanges ?? []) {
    let d = new Date(r.start + "T12:00:00");
    const end = new Date(r.end + "T12:00:00");
    while (d <= end) { blockedDates.add(d.toISOString().slice(0, 10)); d = new Date(d.getTime() + 86400000); }
  }
  const gymDays = new Set<number>(profile?.preferredDays?.gym ?? []);

  // All races the plan covers: every race on the calendar + any added manually.
  const calendarTargets: RaceTarget[] = raceEvents.map(e => ({
    name: e.name,
    date: e.start_date_local.slice(0, 10),
    sport: (e.type as SportType) ?? "Run",
    eventId: e.id,
    priority: racePriority(e),
  }));
  const allRaces: RaceTarget[] = [...calendarTargets, ...manualRaces]
    .map(r => {
      const key = raceKey(r);
      const goalTime = goalTimeOverrides[key] ?? r.goalTime;
      return {
        ...r,
        priority: priorityOverrides[key] ?? r.priority ?? "A",
        distanceKm: distanceKmOverrides[key] ?? r.distanceKm,
        durationHours: parseGoalHours(goalTime) ?? r.durationHours,
        trail: trailOverrides[key] ?? r.trail,
        goalTime,
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));

  // If the athlete's MAIN sport is non-endurance (utförsåkning), shift the engine
  // to the capacity/alpine plan: synthesize a peak-period goal for the upcoming
  // competition season (from the profile's snow months) unless they've already
  // added an explicit non-endurance goal. This is the "peak period as target".
  const primary = profile?.primarySport;
  const showAlpineSeason = !!primary && !isEnduranceSport(primary) && !allRaces.some(r => !isEnduranceSport(r.sport));
  const season = showAlpineSeason ? nextSnowSeason(new Date(), profile?.snowStartMonth, profile?.snowEndMonth) : null;
  // Clamp the chosen peak window to the season; default to the full season.
  const clamp = (v: string | undefined, fallback: string) =>
    v && season && v >= season.start && v <= season.end ? v : fallback;
  const peakStart = season ? clamp(alpinePeak.start, season.start) : "";
  const peakEnd = season ? clamp(alpinePeak.end, season.end) : "";
  const synthGoals: RaceTarget[] = season
    ? [{
        name: primary === "AlpineSki" ? "Alpin säsong (topform)" : `${SPORT_LABELS[primary!]} (topform)`,
        date: peakStart,
        endDate: peakEnd > peakStart ? peakEnd : undefined,
        sport: primary!, priority: "A",
      }]
    : [];
  const racesForPlan: RaceTarget[] = [...allRaces, ...synthGoals];

  // Auto-plan: whenever the race set changes (calendar loaded or a race added),
  // regenerate — no manual selection needed.
  const lastSig = useRef("");
  // km, expected time (parsed to hours) and trail all change the plan, so they're
  // in the regen signature. Goal time is rounded so partial typing doesn't churn.
  const raceSig = racesForPlan.map(r =>
    `${r.name}|${r.date}|${r.endDate ?? ""}|${r.kind ?? ""}|${r.priority}|${r.distanceKm ?? ""}|${r.durationHours != null ? r.durationHours.toFixed(2) : ""}|${r.trail ? 1 : 0}`
  ).join(",");
  const fullSig = `${raceSig}#${profile?.updatedAt ?? ""}`;
  useEffect(() => {
    if (raceSig && fullSig !== lastSig.current) {
      lastSig.current = fullSig;
      onGenerate(racesForPlan);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullSig]);

  const suggestions = manualName.trim().length >= 2
    ? KNOWN_RACES.filter(r => r.name.toLowerCase().includes(manualName.trim().toLowerCase())).slice(0, 6)
    : [];

  function pickSuggestion(r: KnownRace) {
    setManualName(r.name);
    setManualDate(nextOccurrence(r.month, r.day));
    setManualSport(r.sport);
    setShowSuggestions(false);
  }

  function addManualRace() {
    if (!manualName.trim() || !manualDate) return;
    const endDate = NON_ENDURANCE.has(manualSport) && manualEndDate && manualEndDate > manualDate ? manualEndDate : undefined;
    const kind = manualSport === "AlpineSki" && manualIronman ? "ironman" as const : undefined;
    setManualRaces(prev => [...prev, { name: manualName.trim(), date: manualDate, sport: manualSport, endDate, kind }]);
    setManualName(""); setManualDate(""); setManualEndDate(""); setManualIronman(false); setShowManual(false);
  }

  const [savingRace, setSavingRace] = useState(false);
  const [saveRaceStatus, setSaveRaceStatus] = useState<"idle" | "ok" | "error">("idle");

  async function handleSaveRace() {
    if (!manualName.trim() || !manualDate) return;
    setSavingRace(true);
    setSaveRaceStatus("idle");
    try {
      await onCreateRace(manualName.trim(), manualDate, manualSport);
      setSaveRaceStatus("ok");
    } catch (err) {
      console.error("createRace failed:", err);
      setSaveRaceStatus("error");
    }
    setSavingRace(false);
  }

  return (
    <div className="tp">
      <div className="tp__header">
        <button className="tp__back" onClick={onBack}>← Träning</button>
        <h1 className="tp__title">Träningsplanering</h1>
        <div className="tp__header-actions">
          <button className="tp__edit-profile" onClick={onEditProfile}>Min profil</button>
          <button className="tp__edit-profile" onClick={async () => { localStorage.removeItem('planner-profile'); await flushCloudSync(); window.location.reload(); }}>Reset</button>
        </div>
      </div>

      {profile && (
        <div className="tp__profile-bar">
          <span className="tp__profile-sports">
            {profile.sportsEnjoyed.map(s => SPORT_LABELS[s] ?? s).join(" · ")}
          </span>
          <span className="tp__profile-goal-item">· max {profile.maxWeeklyHours ?? 8}h/v</span>
          {profile.raceGoals.filter(r => r.goalTime).map((r, i) => (
            <span key={i} className="tp__profile-goal-item">
              · {r.raceName} <strong>{r.goalTime}</strong>
            </span>
          ))}
          {profile.goals && (
            <span className="tp__profile-goals"> — {profile.goals}</span>
          )}
        </div>
      )}

      <PlannerFlowChart />

      <IntervalBankPanel />

      <IronManPanel />

      {/* Race picker */}
      <section className="tp__section">
        <h2 className="tp__section-title">Välj lopp (ett eller flera)</h2>

        {data === null ? (
          <p className="tp__loading">Laddar…</p>
        ) : (
          <>
            {racesForPlan.length > 0 ? (
              <div className="tp__race-list">
                {racesForPlan.map((r, i) => {
                  const key = raceKey(r);
                  // Non-endurance goals (alpine / IronMan) use the capacity engine —
                  // no distance/goal-time/trail; show the competition period instead.
                  if (!isEnduranceSport(r.sport)) {
                    const isManual = manualRaces.some(m => m.name === r.name && m.date === r.date);
                    const isSynth = !isManual && !r.eventId && !!season;
                    return (
                      <div key={i} className="tp__race-item">
                        <div className="tp__race-btn tp__race-btn--info">
                          <span className="tp__race-name">
                            {r.name}
                            {r.kind === "ironman" && <span className="tp__race-badge"> · IronMan</span>}
                          </span>
                          {!isSynth && (
                            <span className="tp__race-date">{r.endDate ? `${r.date} – ${r.endDate}` : r.date}</span>
                          )}
                          {isManual
                            ? <button className="tp__race-remove" onClick={() => setManualRaces(prev => prev.filter(x => !(x.name === r.name && x.date === r.date)))}>✕</button>
                            : <span className="tp__race-date" style={{ opacity: 0.6 }}>huvudsport</span>}
                        </div>
                        {isSynth ? (
                          <div className="tp__race-extra">
                            <label className="tp__race-field">
                              <span className="tp__race-field-label">Toppform fr.o.m.</span>
                              <input
                                className="tp__race-input" type="date"
                                min={season!.start} max={season!.end}
                                value={peakStart}
                                onChange={e => setAlpinePeak(p => ({ ...p, start: e.target.value }))}
                              />
                            </label>
                            <label className="tp__race-field">
                              <span className="tp__race-field-label">t.o.m.</span>
                              <input
                                className="tp__race-input" type="date"
                                min={peakStart || season!.start} max={season!.end}
                                value={peakEnd}
                                onChange={e => setAlpinePeak(p => ({ ...p, end: e.target.value }))}
                              />
                            </label>
                            <span className="tp__race-field-label" style={{ opacity: 0.7 }}>
                              inom säsongen {season!.start} – {season!.end}
                            </span>
                          </div>
                        ) : (
                          <div className="tp__race-extra">
                            <span className="tp__race-field-label">
                              {SPORT_LABELS[r.sport]} · kapacitetsplan (styrka/power/anaerob){r.endDate ? " — toppar mot periodstart, håller formen perioden ut" : ""}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  }
                  const inferred = inferRaceDistance(r.name, r.sport);
                  const km = distanceKmOverrides[key];
                  const distUnsure = inferred === "unknown" && km == null;
                  return (
                    <div key={i} className="tp__race-item">
                      <div className="tp__race-btn tp__race-btn--info">
                        <span className="tp__race-prio-set">
                          {(["A", "B", "C"] as const).map(p => (
                            <button
                              key={p}
                              className={`tp__prio-opt tp__prio-opt--${p}${r.priority === p ? " tp__prio-opt--active" : ""}`}
                              onClick={() => {
                                setPriorityOverrides(o => ({ ...o, [key]: p }));
                                if (r.eventId != null) onSetRacePriority(r.eventId, p); // save category to intervals.icu
                              }}
                              title={p === "A" ? "Mållopp (egen plan)" : "Form-/deltävling (vävs in)"}
                            >{p}</button>
                          ))}
                        </span>
                        <span className="tp__race-name">{r.name}</span>
                        <span className="tp__race-date">{r.date}</span>
                        {!r.eventId && (
                          <button className="tp__race-remove" onClick={() => setManualRaces(prev => prev.filter(x => !(x.name === r.name && x.date === r.date)))}>✕</button>
                        )}
                      </div>
                      <div className="tp__race-extra">
                        <label className={`tp__race-field${distUnsure ? " tp__race-field--unsure" : ""}`}>
                          <span className="tp__race-field-label">{distUnsure ? "Distans?" : "Distans"}</span>
                          <select
                            className="tp__race-select"
                            value={km ?? ""}
                            onChange={e => {
                              const v = e.target.value;
                              setDistanceKmOverrides(o => {
                                const next = { ...o };
                                if (v) next[key] = parseInt(v, 10); else delete next[key];
                                return next;
                              });
                            }}
                          >
                            <option value="">{inferred === "unknown" ? "Välj…" : `Auto (${BAND_KM[inferred]})`}</option>
                            {DISTANCE_KM_OPTIONS.map(opt => (
                              <option key={opt} value={opt}>{opt} km</option>
                            ))}
                          </select>
                        </label>
                        <label className="tp__race-field">
                          <span className="tp__race-field-label">Måltid</span>
                          <input
                            className="tp__race-input"
                            type="text"
                            placeholder="t.ex. 3:45 / 4h"
                            value={r.goalTime ?? ""}
                            onChange={e => {
                              const v = e.target.value;
                              setGoalTimeOverrides(o => {
                                const next = { ...o };
                                if (v.trim()) next[key] = v; else delete next[key];
                                return next;
                              });
                            }}
                          />
                        </label>
                        <label className="tp__race-field tp__race-field--trail">
                          <input
                            type="checkbox"
                            checked={!!r.trail}
                            onChange={e => {
                              const v = e.target.checked;
                              setTrailOverrides(o => {
                                const next = { ...o };
                                if (v) next[key] = true; else delete next[key];
                                return next;
                              });
                            }}
                          />
                          <span className="tp__race-field-label">Trail/terräng</span>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="tp__loading">Inga lopp i kalendern — lägg till ett nedan.</p>
            )}

            <button
              className={`tp__manual-toggle${showManual ? " tp__manual-toggle--active" : ""}`}
              onClick={() => setShowManual(v => !v)}
            >
              + Lägg till lopp
            </button>

            {showManual && (
              <div className="tp__manual-form">
                <div className="tp__autocomplete">
                  <input
                    className="tp__input"
                    type="text"
                    placeholder="Skriv loppets namn (t.ex. Lidin…)"
                    value={manualName}
                    onChange={e => { setManualName(e.target.value); setShowSuggestions(true); }}
                    onFocus={() => setShowSuggestions(true)}
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="tp__suggestions">
                      {suggestions.map(s => (
                        <button key={s.name} className="tp__suggestion" onClick={() => pickSuggestion(s)}>
                          <span>{s.name}</span>
                          <span className="tp__suggestion-date">{nextOccurrence(s.month, s.day)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <input
                  className="tp__input"
                  type="date"
                  value={manualDate}
                  onChange={e => setManualDate(e.target.value)}
                />
                <select
                  className="tp__select"
                  value={manualSport}
                  onChange={e => setManualSport(e.target.value as SportType)}
                >
                  {SPORT_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                {NON_ENDURANCE.has(manualSport) && (
                  <label className="tp__period" style={{ flex: "1 1 100%", fontSize: "0.8rem", opacity: 0.8 }}>
                    Tävlingsperiod t.o.m. (valfritt) — planen toppar mot startdatumet och håller formen perioden ut
                    <input
                      className="tp__input"
                      type="date"
                      value={manualEndDate}
                      min={manualDate || undefined}
                      onChange={e => setManualEndDate(e.target.value)}
                    />
                  </label>
                )}
                {manualSport === "AlpineSki" && (
                  <label style={{ flex: "1 1 100%", fontSize: "0.8rem", opacity: 0.85, display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <input
                      type="checkbox"
                      checked={manualIronman}
                      onChange={e => {
                        setManualIronman(e.target.checked);
                        if (e.target.checked && !manualName.trim()) setManualName("IronMan-test");
                      }}
                    />
                    IronMan-test (fystest) — tränar alla åtta testmoment, se IronMan-panelen
                  </label>
                )}
                {manualName.trim() && manualDate && (
                  <>
                    <button
                      className="tp__btn tp__btn--primary"
                      style={{ flex: "1 1 100%", marginTop: "0.25rem" }}
                      onClick={addManualRace}
                    >
                      Lägg till i planen
                    </button>
                    <button
                      className="tp__btn tp__btn--publish"
                      style={{ flex: "1 1 100%", marginTop: "0.25rem" }}
                      disabled={savingRace}
                      onClick={handleSaveRace}
                    >
                      {savingRace ? "Sparar…" : saveRaceStatus === "ok" ? "Sparat!" : saveRaceStatus === "error" ? "Misslyckades – försök igen" : "Spara lopp till intervals.icu"}
                    </button>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </section>

      {/* Plan sections — one per race */}
      {plans.map((plan, i) => {
        // Window starts at today (not the first session) so stray existing
        // events before the first session also show as faded "ersätts" — matching
        // exactly what publish will wipe.
        // Each plan owns [windowStart, race] — a chained race must NOT reach back
        // to today (that would mark the whole preceding timeline as "removed").
        const todayStr = new Date().toISOString().slice(0, 10);
        const firstSession = plan.sessions.length > 0 ? plan.sessions[0].date : plan.race.date;
        const rangeStart = plan.windowStart ?? (todayStr < firstSession ? todayStr : firstSession);
        const rangeEnd = plan.race.endDate ?? plan.race.date;
        const calDates = new Set((data?.events ?? []).filter(e => !isRaceEvent(e)).map(e => e.start_date_local.slice(0, 10)));
        const newSessions = plan.sessions.filter(s => !calDates.has(s.date));
        const totalSessions = newSessions.length;
        const totalTSS = newSessions.reduce((s, x) => s + x.estimatedTSS, 0);
        return (
          <div key={i}>
            <section className="tp__section">
              <h2 className="tp__section-title">
                {plan.race.name} — {plan.race.date}
                {plan.weeks.length > 0 && (
                  <span className={`tp__tsb-badge tp__tsb-badge--${plan.expectedRaceTSB >= 15 && plan.expectedRaceTSB <= 25 ? "good" : plan.expectedRaceTSB > 25 ? "high" : "low"}`}>
                    TSB loppdag: {plan.expectedRaceTSB > 0 ? "+" : ""}{plan.expectedRaceTSB}
                  </span>
                )}
                {plan.weeks.length > 0 && (
                  <span className="tp__tsb-badge tp__tsb-badge--info">
                    Z4+ {plan.historicalHardPct != null ? `${plan.historicalHardPct}% historiskt → ` : ""}{plan.plannedHardPct}% i plan
                  </span>
                )}
              </h2>
              {plan.volumeNote && (
                <div className="tp__volume-note">{plan.volumeNote}</div>
              )}
              {plan.weeks.length === 0 ? (
                <p className="tp__empty">Loppet är för nära eller passerat — ingen plan.</p>
              ) : (
                <PlanCalendar sessions={plan.sessions} planWeeks={plan.weeks} calendarEvents={data?.events ?? []} flaggedDates={plan.flaggedDates} rangeStart={rangeStart} rangeEnd={rangeEnd} blockedDates={blockedDates} gymDays={gymDays} />
              )}
            </section>

            {plan.weeks.length > 0 && (
              <div className="tp__publish-row">
                {plan.published ? (
                  <div className="tp__published">Plan publicerad!</div>
                ) : (
                  <>
                    <button
                      className="tp__btn tp__btn--publish"
                      disabled={plan.publishing}
                      onClick={() => onPublish(i)}
                    >
                      {plan.publishing ? "Publicerar…" : "Publicera till intervals.icu"}
                    </button>
                    {plan.progress && (
                      <div className="tp__progress">
                        <div
                          className="tp__progress-bar"
                          style={{ width: `${(plan.progress.done / plan.progress.total) * 100}%` }}
                        />
                        <span className="tp__progress-label">
                          {plan.progress.phase === "clean"
                            ? `Rensar ${plan.progress.done} / ${plan.progress.total}`
                            : `${plan.progress.done} / ${plan.progress.total} pass`}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
